<template>
  <div>
    <form-wizard
      ref="form-wizard"
      color="#1dd1a1"
      :title="null"
      :subtitle="null"
      layout="vertical"
      :finish-button-text="$t('send_application')"
      :back-button-text="$t('previous')"
      :next-button-text="$t('next')"
      class="vertical-steps steps-transparent"
      :start-index="startIndex"
      @on-complete="formSubmitted"
    >
      <!-- basic -->
      <tab-content
        :title="$t('basic_info')"
        icon="feather icon-user"
        :before-change="validationFormBasic"
      >
        <validation-observer
          ref="basicRules"
          tag="form"
        >
          <b-row>
            <b-col
              cols="12"
              class="mb-2"
            >
              <h5 class="mb-0">
                {{ $t('basic_info') }}
              </h5>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('first_name')"
                label-for="first_name"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('first_name')"
                  rules="required"
                >
                  <b-form-input
                    id="first_name"
                    v-model="form.firstName"
                    :state="errors.length > 0 ? false:null"
                    placeholder=""
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('last_name')"
                label-for="last_name"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('last_name')"
                  rules="required"
                >
                  <b-form-input
                    id="last_name"
                    v-model="form.lastName"
                    :state="errors.length > 0 ? false:null"
                    placeholder=""
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('birthday')"
                label-for="birthday"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('birthday')"
                  rules="required"
                >
                  <b-form-input
                    v-model="birthday"
                    v-mask="['##.##.####']"
                    :placeholder="$t('date_placeholder')"
                    :state="errors.length > 0 || dateInvalid(birthday) ? false : null"
                  />
                  <small class="text-danger">{{ errors[0] || dateInvalid(birthday) }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('gender')"
                label-for="gender"
              >
                <div class="inline-form-group">
                  <b-form-radio
                    v-model="form.gender"
                    plain
                    name="female"
                    value="female"
                  >
                    {{ $t('woman') }}
                  </b-form-radio>
                  <b-form-radio
                    v-model="form.gender"
                    plain
                    name="male"
                    value="male"
                    class="custom-control-primary"
                  >
                    {{ $t('man') }}
                  </b-form-radio>
                </div>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('email')"
                label-for="email"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('email')"
                  rules="required|email"
                >
                  <b-form-input
                    id="email"
                    v-model="form.email"
                    :state="errors.length > 0 ? false:null"
                    type="email"
                    :disabled="startIndex > 1"
                    placeholder=""
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('phone')"
                label-for="mobile"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('phone')"
                  rules="required"
                >
                  <b-form-input
                    id="mobile"
                    v-model="form.mobile"
                    :state="errors.length > 0 ? false:null"
                    placeholder=""
                    type="number"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </validation-observer>
      </tab-content>
      <!-- details -->
      <tab-content
        :title="$t('more_info')"
        icon="feather icon-info"
        :before-change="validationFormDetails"
      >
        <validation-observer
          ref="detailsRules"
          tag="form"
        >
          <b-row>
            <b-col md="6">
              <b-form-group
                :label="$t('finnish')"
                label-for="finnish-language"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('finnish')"
                  rules="required"
                >
                  <v-select
                    id="finnish-language"
                    v-model="finnish"
                    :state="errors.length > 0 ? false:null"
                    :options="finnishLevelOptions"
                    :selectable="option => ! option.value.includes('valitse')"
                    label="text"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('language_proficiency')"
                label-for="languages"
              >
                <v-select
                  id="languages"
                  v-model="languages"
                  :options="languagesOptions"
                  :selectable="option => ! option.value.includes('valitse')"
                  label="text"
                  multiple
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('car_available')"
                label-for="car-available"
              >
                <b-form-checkbox
                  v-model="form.carAvailable"
                  name="car-available"
                  switch
                  inline
                  style="margin-top: 10px"
                >
                  <span class="switch-icon-left">
                    <feather-icon icon="CheckIcon" />
                  </span>
                  <span class="switch-icon-right">
                    <feather-icon icon="XIcon" />
                  </span>
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('driving_license')"
                label-for="driving-license"
              >
                <v-select
                  id="driving-license"
                  v-model="drivingLicense"
                  :options="drivingLicenseOptions"
                  :selectable="option => ! option.value.includes('valitse')"
                  label="text"
                  multiple
                />
              </b-form-group>
            </b-col>
          </b-row>
        </validation-observer>
      </tab-content>
      <!-- address -->
      <tab-content
        :title="$t('address_info')"
        icon="feather icon-map-pin"
        :before-change="validationFormAddress"
      >
        <validation-observer
          ref="addressRules"
          tag="form"
        >
          <b-row>
            <b-col
              cols="12"
              class="mb-2"
            >
              <h5 class="mb-0">
                {{ $t('address_info') }}
              </h5>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('street_address')"
                label-for="street-address"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('street_address')"
                  rules="required"
                >
                  <b-form-input
                    id="street-address"
                    v-model="form.address.streetAddress"
                    :state="errors.length > 0 ? false:null"
                    placeholder=""
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('city')"
                label-for="city"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('city')"
                  rules="required"
                >
                  <b-form-input
                    id="city"
                    v-model="form.address.city"
                    :state="errors.length > 0 ? false:null"
                    placeholder=""
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('postcode')"
                label-for="postcode"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('postcode')"
                  rules="required"
                >
                  <b-form-input
                    id="postcode"
                    v-model="form.address.postcode"
                    :state="errors.length > 0 ? false:null"
                    placeholder=""
                    type="number"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('state')"
                label-for="state"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('state')"
                  rules="required"
                >
                  <v-select
                    id="state"
                    v-model="state"
                    :state="errors.length > 0 ? false:null"
                    :options="stateOptions"
                    :selectable="option => !option.value.includes($t('choose'))"
                    label="text"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </validation-observer>
      </tab-content>
      <!-- work -->
      <tab-content
        :title="$t('work')"
        icon="feather icon-briefcase"
        :before-change="validationFormWork"
      >
        <validation-observer
          ref="workRules"
          tag="form"
        >
          <b-row>
            <b-col
              cols="12"
              class="mb-2"
            >
              <h5 class="mb-0">
                {{ $t('work_history') }}
              </h5>
            </b-col>
            <b-col md="12">
              <validation-provider
                #default="{ errors }"
                :name="$t('work_history')"
                rules="required"
              >
                <b-form-textarea
                  id="workhistory"
                  v-model="form.workHistory"
                  :state="errors.length > 0 ? false:null"
                  :placeholder="$t('tell_work_history')"
                  rows="3"
                  max-rows="6"
                  style="margin-bottom: 20px"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('profession')"
                label-for="role"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('profession')"
                  rules="required"
                >
                  <v-select
                    id="role"
                    v-model="role"
                    style="text-transform: capitalize"
                    :state="errors.length > 0 ? false:null"
                    :options="typeReqOptions"
                    :selectable="option => ! option.includes('valitse')"
                    label="text"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
              md="6"
            >
              <b-form-group
                v-if="role == 'muu'"
                :label="$t('other')"
                label-for="other-profession"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('other')"
                  rules="required"
                >
                  <b-form-input
                    id="other-profession"
                    v-model="otherProfession"
                    :state="errors.length > 0 ? false:null"
                    placeholder=""
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-row v-if="role == 'opiskelija'">
                <b-col md="6">
                  <b-form-group
                    :label="$t('what_is_studying')"
                    label-for="study-subject"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :name="$t('what_is_studying')"
                      rules="required"
                    >
                      <b-form-input
                        id="study-subject"
                        v-model="form.studies.studyType"
                        :state="errors.length > 0 ? false:null"
                        placeholder=""
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('course_credits')"
                    label-for="credit"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :name="$t('course_credits')"
                      rules="required"
                    >
                      <b-form-input
                        id="credits"
                        v-model="form.studies.credits"
                        :state="errors.length > 0 ? false:null"
                        placeholder="Esim: 20"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('other_permits')"
                :description="$t('separate_permits_press_enter')"
                label-for="permits"
              >
                <v-select
                  id="permits"
                  v-model="form.permits"
                  label="text"
                  multiple
                  taggable
                  push-tags
                  :placeholder="$t('for_example_love')"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('medicine_permit')"
                label-for="love"
              >
                <b-form-checkbox
                  v-model="form.love"
                  name="love"
                  switch
                  inline
                  style="margin-top: 10px"
                >
                  <span class="switch-icon-left">
                    <feather-icon icon="CheckIcon" />
                  </span>
                  <span class="switch-icon-right">
                    <feather-icon icon="XIcon" />
                  </span>
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                :label="$t('what_are_you_looking_for')"
                label-for="contracts"
              >
                <v-select
                  id="contracts"
                  v-model="seeking"
                  label="text"
                  multiple
                  :placeholder="$t('choose_work_type')"
                  :options="contractOptions"
                  :reduce="option => option.value"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <validation-provider
                v-if="seeking.includes('long-term') || seeking.includes('periodic')"
                #default="{ errors }"
                :name="$t('when_can_start')"
                rules="required"
              >
                <b-form-group
                  :label="$t('when_can_start')"
                >
                  <b-form-input
                    v-model="seekingStart"
                    v-mask="['##.##.####']"
                    placeholder="PP.KK.VVVV"
                    :state="errors.length > 0 || dateInvalid(seekingStart) ? false : null"
                  />
                </b-form-group>
                <small class="text-danger">{{ errors[0] || dateInvalid(seekingStart) }}</small>
              </validation-provider>
            </b-col>
            <b-col md="6">
              <validation-provider
                v-if="seeking.includes('periodic')"
                #default="{ errors }"
                :name="$t('how_long_temp_work')"
                rules="required"
              >
                <b-form-group
                  :label="$t('how_long_temp_work')"
                >
                  <b-form-input
                    v-model="seekingEnd"
                    v-mask="['##.##.####']"
                    :placeholder="$t('date_placeholder')"
                    :state="errors.length > 0 || dateInvalid(seekingEnd) ? false : null"
                  />
                </b-form-group>
                <small class="text-danger">{{ errors[0] || dateInvalid(seekingEnd) }}</small>
              </validation-provider>
            </b-col>
          </b-row>
        </validation-observer>
      </tab-content>
      <!-- verification -->
      <tab-content
        v-if="!verified"
        :title="$t('confirmation')"
        icon="feather icon-user-check"
        :before-change="verification"
      >
        <validation-observer
          ref="verificationRules"
          tag="form"
        >
          <b-row>
            <b-col
              cols="12"
              class="mb-2"
            >
              <h5 class="mb-0">
                {{ $t('confirm_email') }}
              </h5>
            </b-col>
            <b-col
              cols="12"
              class="mb-2"
            >
              <p>
                {{ $t('confirm_email_OTP') }}
              </p>
              <b-button
                variant="info"
                style="margin-right: 10px"
                :disabled="sendingOTP"
                @click="sendOTP"
              >
                <div
                  v-if="sendingOTP"
                  class="d-flex justify-content-center"
                >
                  <b-spinner
                    variant="light"
                    small
                    :label="$t('sending')"
                  />
                  <span class="ml-1">{{ $t('sending') }}</span>
                </div>
                <span v-if="!sendingOTP">{{ $t('send_code') }}</span>
              </b-button>
              <span style="font-size: 14px">
                <b>{{ form.email }} </b>
              </span>
              <p
                v-if="!sendingOTP && OTPSent"
                style="margin-top: 5px; color:rgb(29, 209, 161)"
              >
                {{ $t('OTP_code_sent') }}
              </p>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="$t('OTP_code')"
                label-for="otp"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('OTP_code')"
                  rules="required"
                >
                  <b-form-input
                    id="otp"
                    v-model="OTP"
                    :state="errors.length > 0 ? false:null"
                    :placeholder="$t('OTP_code')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </validation-observer>
      </tab-content>
      <!-- files -->
      <tab-content
        :title="$t('send')"
        icon="feather icon-check-circle"
      >
        <b-row>
          <b-col
            cols="12"
            class="mb-2"
          >
            <h5 class="mb-0">
              {{ $t('send') }}
            </h5>
            <!----<small class="text-muted"></small>-->
          </b-col>
          <b-col
            cols="12"
            class="mb-2"
          >
            <!----<b-button
              :disabled="files.length > 6"
              @click="uploadFile"
            >
              Lisää Tiedosto
            </b-button>
            <b-form-file
              ref="file-input"
              v-model="file"
              hidden
              placeholder=""
              no-drop
              plain
              :disabled="files.length > 6"
              accept="image/jpeg, image/png, application/pdf"
            />-->
          </b-col>
          <!---<b-col md="12">
            <b-row
              v-for="(item, index) in files"
              :key="item.name"
            >
              <b-col
                md="10"
              >
                <p>{{ item.name }}</p>
              </b-col>
              <b-col
                md="2"
              >
                <b-button
                  variant="outline-danger"
                  class="btn-icon"
                  size="sm"
                  @click="removeItem(index)"
                >
                  <feather-icon
                    icon="XIcon"
                    class="mr-25"
                  />
                </b-button>
              </b-col>
            </b-row>
          </b-col>-->
        </b-row>
      </tab-content>
    </form-wizard>

  </div>
</template>

<script>
import { FormWizard, TabContent } from 'vue-form-wizard'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email } from '@validations'
import vSelect from 'vue-select'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import * as moment from 'moment'
import 'moment-timezone'
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormCheckbox,
  BFormTextarea,
  BSpinner,
  // BFormFile,
  BButton,
} from 'bootstrap-vue'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'
import { mask } from 'vue-the-mask'

export default {
  components: {
    FormWizard,
    TabContent,
    BRow,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BFormTextarea,
    BSpinner,
    // BFormFile,
    BButton,
    vSelect,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    mask,
  },
  props: {
    startIndex: {
      type: Number,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    otherProf: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      required,
      email,
      file: null,
      OTP: null,
      sendingOTP: false,
      OTPSent: false,
      verified: false,
      dateFormat: 'DD.MM.YYYY',
      // Select variables
      state: this.form.address.state ? this.form.address.state : '',
      finnish: this.form.finnish ? this.form.finnish : '',
      languages: this.form.languages ? this.form.languages : [],
      drivingLicense: this.form.drivingLicense ? this.form.drivingLicense : '',
      role: this.form.profession ? this.form.profession : '',
      otherProfession: this.otherProf,
      permits: this.form.permits ? this.form.permits : [],
      birthday: this.form.birthday ? this.form.birthday : '',
      seeking: this.form.seeking ? this.form.seeking : [],
      seekingStart: null,
      seekingEnd: null,
      files: [],
      finnishLevelOptions: [
        { text: this.$i18n.t('choose'), value: 'valitse' },
        { text: this.$i18n.t('mother_tongue'), value: 'äidinkieli' },
        { text: this.$i18n.t('excellent'), value: 'erinomainen' },
        { text: this.$i18n.t('good'), value: 'hyvä' },
        { text: this.$i18n.t('basic'), value: 'perus' },
        { text: this.$i18n.t('beginner'), value: 'alkeet' },
        { text: this.$i18n.t('no'), value: 'ei' },
      ],
      languagesOptions: [
        { value: 'valitse', text: this.$i18n.locale === 'fi' ? 'Valitse' : 'Wähle' },
        { value: 'english', text: 'English' },
        { value: 'swedish', text: 'Swedish' },
        { value: 'somali', text: 'Somali' },
        { value: 'arabic', text: 'Arabic' },
        { value: 'spanish', text: 'Spanish' },
        { value: 'russian', text: 'Russian' },
      ],
      drivingLicenseOptions: [
        { text: 'B', value: 'b' },
        { text: 'C', value: 'c' },
        { text: 'A', value: 'a' },
      ],
      stateOptions: [
        { text: 'Uusimaa', value: 'uusimaa' },
        { text: 'Varsinais-Suomi', value: 'varsinais-suomi' },
        { text: 'Satakunta', value: 'satakunta' },
        { text: 'Päijät-Häme', value: 'päijät-häme' },
        { text: 'Pohjois-Savo', value: 'pohjois-savo' },
        { text: 'Pohjois-Pohjanmaa', value: 'pohjois-pohjanmaa' },
        { text: 'Pohjois-Karjala', value: 'pohjois-karjala' },
        { text: 'Pohjanmaa', value: 'pohjanmaa' },
        { text: 'Pirkanmaa', value: 'pirkanmaa' },
        { text: 'Lappi', value: 'lappi' },
        { text: 'Kymenlaakso', value: 'kymenlaakso' },
        { text: 'Keski-Suomi', value: 'keski-suomi' },
        { text: 'Keski-Pohjanmaa', value: 'keski-pohjanmaa' },
        { text: 'Kanta-Häme', value: 'kanta-häme' },
        { text: 'Kainuu', value: 'kainuu' },
        { text: 'Etelä-Savo', value: 'etelä-savo' },
        { text: 'Etelä-Pohjanmaa', value: 'etelä-pohjanmaa' },
        { text: 'Etelä-Karjala', value: 'etelä-karjala' },
        { text: 'Ahvenanmaa', value: 'ahvenanmaa' },
      ],
      germanStateOptions: [
        { text: 'Baden-Württemberg', value: 'baden-württemberg' },
        { text: 'Bayern', value: 'bayern' },
        { text: 'Berlin', value: 'berlin' },
        { text: 'Brandenburg', value: 'brandenburg' },
        { text: 'Bremen', value: 'bremen' },
        { text: 'Hamburg', value: 'hamburg' },
        { text: 'Hessen', value: 'hessen' },
        { text: 'Mecklenburg-Vorpommern', value: 'mecklenburg-vorpommern' },
        { text: 'Niedersachsen', value: 'niedersachsen' },
        { text: 'Nordrhein-Westfalen', value: 'nordrhein-westfalen' },
        { text: 'Rheinland-Pfalz', value: 'rheinland-pfalz' },
        { text: 'das Saarland', value: 'das saarland' },
        { text: 'Sachsen', value: 'sachsen' },
        { text: 'Sachsen-Anhalt', value: 'sachsen-anhalt' },
        { text: 'Schleswig-Holstein', value: 'schleswig-Holstein' },
        { text: 'Thüringen', value: 'thüringen' },
      ],
      contractOptions: [
        { text: this.$i18n.t('gig'), value: 'gig' },
        { text: this.$i18n.t('long-term'), value: 'long-term' },
        { text: this.$i18n.t('periodic'), value: 'periodic' },
      ],
    }
  },
  computed: {
    typeReqOptions() {
      const roles = this.$store.state.appAccount.roles.map(role => role.label)
      roles.push('opiskelija', 'muu')
      return roles
    },
  },
  watch: {
    file(newVal) {
      if (newVal) {
        if (newVal.size < 15184495) {
          this.files.push(newVal)
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Liian suuri tiedosto',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        }
      }
    },
  },
  mounted() {
    if (this.startIndex > 0) {
      this.$refs['form-wizard'].activateAll()
    }
  },
  methods: {
    sendOTP() {
      this.sendingOTP = true
      this.$emit('verifyEmail', {
        email: this.form.email,
        success: () => {
          this.$emit('sendOTP', {
            email: this.form.email,
            success: () => {
              this.sendingOTP = false
              this.OTPSent = true
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'OTP lähetetty sähköpostiisi',
                  icon: 'CheckIcon',
                  variant: 'success',
                },
              })
            },
            fail: () => {
              this.sendingOTP = false
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Something went wrong',
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              })
            },
          })
        },
        fail: () => {
          this.sendingOTP = false
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Sähköposti on jo käytössä',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          })
        },
      })
    },
    verification() {
      return new Promise((resolve, reject) => {
        this.$refs.verificationRules.validate().then(success => {
          if (success) {
            this.$emit('verifyOTP', {
              email: this.form.email,
              OTP: this.OTP,
              success: () => {
                this.verified = true
                resolve(true)
              },
              fail: () => {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: 'Väärä OTP',
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
                reject()
              },
            })
          } else {
            reject()
          }
        })
      })
    },
    formSubmitted() {
      // Parse selection data
      this.form.firstName = this.form.firstName.toLowerCase()
      this.form.lastName = this.form.lastName.toLowerCase()
      this.form.address.state = this.state.value
      this.form.address.city = this.form.address.city.toLowerCase()
      this.form.finnish = this.finnish.value
      this.form.languages = this.languages.map(item => item.value)
      this.form.drivingLicense = this.drivingLicense.map(item => item.value)
      this.form.birthday = this.birthday
      this.form.application = {
        seeking: this.seeking,
        start: this.seekingStart,
        end: this.seekingEnd,
      }
      if (this.role !== 'muu') {
        this.form.profession = this.role
      } else {
        this.form.profession = this.otherProfession.toLowerCase()
      }

      this.$emit('submit', {
        form: this.form,
        files: this.files,
        OTP: this.OTP,
        uid: this.uid,
      })
    },
    uploadFile() {
      this.$refs['file-input'].$el.click()
    },
    removeItem(index) {
      this.files.splice(index, 1)
    },
    validationFormBasic() {
      return new Promise((resolve, reject) => {
        this.$refs.basicRules.validate().then(success => {
          if (success && !this.dateInvalid(this.birthday)) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validationFormDetails() {
      return new Promise((resolve, reject) => {
        this.$refs.detailsRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validationFormAddress() {
      return new Promise((resolve, reject) => {
        this.$refs.addressRules.validate().then(success => {
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validationFormWork() {
      return new Promise((resolve, reject) => {
        this.$refs.workRules.validate().then(success => {
          if ((this.seeking.includes('long-term') || this.seeking.includes('periodic')) && this.dateInvalid(this.seekingStart)) {
            reject()
          }
          if (this.seeking.includes('periodic') && this.dateInvalid(this.seekingEnd)) {
            reject()
          }
          if (success) {
            resolve(true)
          } else {
            reject()
          }
        })
      })
    },
    validationFormFiles() {
      return true
      /* let valid = false
      if (this.files.length > 0) {
        // this.$emit('submit', { form: this.form, files: this.files })
        valid = true
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Lisää CV',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
      }
      return valid */
    },
    dateInvalid(date) {
      if (!date || moment(date, this.dateFormat, true).isValid()) {
        return ''
      }
      return this.$i18n.locale === 'fi' ? 'Päivämäärä pitää olla muodossa, esim: 01.01.1990' : 'Bitte verfasse das Datum im Format DD.MM.YYYY'
    },
  },
}
</script>
<style lang="scss">
  .inline-form-group {
    display: flex;
    justify-content: space-between;
    max-width: 160px;
  }
</style>
